import * as React from 'react'
import {
    Alert,
    Box,
    Button,
    Chip,
    FormControl,
    InputAdornment,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { ActionType, ErrorType } from '../types'
import useAutoTranslate from '../hooks/useAutoTranslate'
import { useSmoothLiveNow } from '../utils/liveClock'

const DEFAULT_APPROVAL_DURATION_MS = 24 * 60 * 60 * 1000
const ACCOUNT_ROLE_OPTIONS = Object.freeze([
    { value: 2, key: 'Administrator' },
    { value: 1, key: 'Moderator' },
    { value: 0, key: 'Operator' }
])

const pad = value => String(value).padStart(2, '0')

const formatDateTimeInputValue = value => {
    const timestamp = Number(value)
    if (!Number.isFinite(timestamp) || timestamp <= 0)
        return ''

    const date = new Date(timestamp)
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const formatRemainingDuration = durationMs => {
    if (!Number.isFinite(durationMs) || durationMs <= 0)
        return 'Expired'

    const totalSeconds = Math.floor(durationMs / 1000)
    const days = Math.floor(totalSeconds / (24 * 60 * 60))
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    if (days > 0)
        return `${days}d ${hours}h`
    if (hours > 0)
        return `${hours}h ${minutes}m ${seconds}s`
    if (minutes > 0)
        return `${minutes}m ${seconds}s`

    return `${seconds}s`
}

const getApprovalStatus = (user, now) => {
    const approvedUntil = Number(user?.approvedUntil)

    if (Boolean(user?.approved)) {
        if (!Number.isFinite(approvedUntil) || approvedUntil <= 0)
            return { label: 'Permanent', tone: 'permanent' }

        const remainingMs = approvedUntil - now
        if (remainingMs <= 0)
            return { label: 'Expired', tone: 'expired' }

        if (remainingMs <= 60 * 60 * 1000)
            return { label: formatRemainingDuration(remainingMs), tone: 'urgent' }

        if (remainingMs <= 24 * 60 * 60 * 1000)
            return { label: formatRemainingDuration(remainingMs), tone: 'soon' }

        return { label: formatRemainingDuration(remainingMs), tone: 'healthy' }
    }

    if (Number.isFinite(approvedUntil) && approvedUntil > 0) {
        if (approvedUntil <= now)
            return { label: 'Expired', tone: 'expired' }

        return { label: `Disabled - ${formatRemainingDuration(approvedUntil - now)}`, tone: 'disabled' }
    }

    return { label: 'Not approved', tone: 'inactive' }
}

const getApprovalChipSx = tone => {
    switch (tone) {
        case 'permanent':
            return {
                backgroundColor: 'rgba(110, 212, 165, 0.16)',
                color: '#6ed4a5',
                border: '1px solid rgba(110, 212, 165, 0.34)'
            }
        case 'healthy':
            return {
                backgroundColor: 'rgba(88, 185, 255, 0.14)',
                color: '#7fc7ff',
                border: '1px solid rgba(88, 185, 255, 0.28)'
            }
        case 'soon':
            return {
                backgroundColor: 'rgba(123, 176, 255, 0.16)',
                color: '#9ab8ff',
                border: '1px solid rgba(123, 176, 255, 0.32)'
            }
        case 'urgent':
            return {
                backgroundColor: 'rgba(255, 180, 92, 0.16)',
                color: '#ffcb82',
                border: '1px solid rgba(255, 180, 92, 0.34)'
            }
        case 'expired':
            return {
                backgroundColor: 'rgba(244, 132, 132, 0.16)',
                color: '#f7a2a2',
                border: '1px solid rgba(244, 132, 132, 0.34)'
            }
        case 'disabled':
            return {
                backgroundColor: 'rgba(170, 176, 193, 0.12)',
                color: '#c8cedd',
                border: '1px solid rgba(170, 176, 193, 0.24)'
            }
        default:
            return {
                backgroundColor: 'rgba(255,255,255,0.06)',
                color: '#c8cedd',
                border: '1px solid rgba(255,255,255,0.12)'
            }
    }
}

export default function AdminPage({ ws, users, onClose, accountInfo, liveNow, appLanguage = 'en', mode = 'admin' }) {
    const [searchTerm, setSearchTerm] = React.useState('')
    const deferredSearchTerm = React.useDeferredValue(searchTerm)
    const isModeratorMode = mode === 'moderator'
    const pageTitle = isModeratorMode ? 'Moderator' : 'Admin Dashboard'
    const pageDescription = isModeratorMode ?
        'Moderators can approve users, update Approved until, and manage CALL permissions. They still cannot revoke the main approval or change administrative access.' :
        'Manage user access and server status. Users must be APPROVED to access the bot.'

    const currentAccountUsers = React.useMemo(
        () => (Array.isArray(users) ? users : [])
            .filter(user => user?.uuid && accountInfo?.uuid && user.uuid === accountInfo.uuid)
            .sort((left, right) => String(left?.name ?? '').localeCompare(String(right?.name ?? ''))),
        [accountInfo?.uuid, users]
    )

    const linkedAccounts = React.useMemo(
        () => [...new Set(
            currentAccountUsers
                .map(user => String(user?.name ?? '').trim())
                .filter(Boolean)
        )],
        [currentAccountUsers]
    )

    const normalizedSearchTerm = deferredSearchTerm.trim().toLowerCase()

    const adminApprovalGroups = React.useMemo(() => {
        const grouped = new Map()

        ;(Array.isArray(users) ? users : []).forEach(user => {
            const ownerUsername = String(user?.ownerUsername ?? '').trim() || String(user?.uuid ?? '').trim() || 'Unknown user'
            const groupKey = `${ownerUsername}::${String(user?.uuid ?? '')}`
            const current = grouped.get(groupKey) ?? {
                key: groupKey,
                ownerUsername,
                uuid: String(user?.uuid ?? ''),
                ownerPrivilege: Number(user?.ownerPrivilege ?? 0),
                ownerRole: String(user?.ownerRole ?? ''),
                linkedUsers: []
            }

            current.linkedUsers.push(user)
            grouped.set(groupKey, current)
        })

        return [...grouped.values()]
            .map(group => ({
                ...group,
                linkedUsers: [...group.linkedUsers].sort((left, right) =>
                    String(left?.name ?? '').localeCompare(String(right?.name ?? ''))
                ),
                pendingApprovals: group.linkedUsers.filter(user => !Boolean(user?.approved)).length
            }))
            .sort((left, right) => left.ownerUsername.localeCompare(right.ownerUsername))
    }, [users])

    const filteredApprovalGroups = React.useMemo(
        () => adminApprovalGroups.filter(group => {
            if (!normalizedSearchTerm)
                return true

            const searchSource = [
                group.ownerUsername,
                group.uuid,
                ...group.linkedUsers.map(user => user?.name),
                ...group.linkedUsers.map(user => user?.uuid)
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()

            return searchSource.includes(normalizedSearchTerm)
        }),
        [adminApprovalGroups, normalizedSearchTerm]
    )

    const filteredUsers = React.useMemo(
        () => (Array.isArray(users) ? users : []).filter(user => {
            if (!normalizedSearchTerm)
                return true

            const searchSource = [
                user?.ownerUsername,
                user?.name,
                user?.uuid
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()

            return searchSource.includes(normalizedSearchTerm)
        }),
        [normalizedSearchTerm, users]
    )

    const t = useAutoTranslate(appLanguage, [
        'Expired',
        'Permanent',
        'Disabled',
        'Not approved',
        'Admin Dashboard',
        'Moderators can approve users, update Approved until, and manage CALL permissions. They still cannot revoke the main approval or change administrative access.',
        'Close',
        'Manage user access and server status. Users must be APPROVED to access the bot.',
        'User Management',
        'Username',
        'UUID',
        'Status',
        'Approved',
        'CALL',
        'Time Left',
        'Actions',
        'Running',
        'Stopped',
        'Yes',
        'No',
        'Approved until',
        'Alliance CALL',
        'Alliance RIFT Portal',
        'Grand Tournament',
        'Delete',
        'My Account',
        'Connected username',
        'Unknown user',
        'Administrator',
        'Moderator',
        'Operator',
        'Unavailable',
        'Role',
        'Linked accounts',
        'Linked account details',
        'No linked accounts',
        'Search approvals...',
        'Approval overview',
        'Pending approvals',
        'No matching users',
        'Owner username',
        'Account role',
        'Allow Alliance Call Alert'
    ])

    const resolveRoleLabel = React.useCallback(roleName => {
        const normalizedRole = String(roleName ?? '').trim().toLowerCase()

        if (normalizedRole === 'administrator')
            return t('Administrator')

        if (normalizedRole === 'moderator')
            return t('Moderator')

        return t('Operator')
    }, [t])

    const effectiveNow = useSmoothLiveNow(liveNow)

    const sendUserUpdate = (user, updates) => {
        const payload = { ...user, ...updates, uuid: user.uuid }
        ws.send(JSON.stringify([ErrorType.Success, ActionType.SetUser, payload]))
    }

    const toggleApproval = user => {
        if (isModeratorMode && Boolean(user?.approved))
            return

        const nextApproved = !Boolean(user.approved)
        const currentApprovalUntil = Number(user?.approvedUntil)
        const nextApprovalUntil = nextApproved && (!Number.isFinite(currentApprovalUntil) || currentApprovalUntil <= effectiveNow) ?
            effectiveNow + DEFAULT_APPROVAL_DURATION_MS :
            user?.approvedUntil

        if (isModeratorMode) {
            if (!nextApproved)
                return

            ws.send(JSON.stringify([ErrorType.Success, ActionType.ApproveModerationUser, {
                uuid: user.uuid,
                id: user.id,
                approvedUntil: nextApprovalUntil
            }]))
            return
        }

        sendUserUpdate(user, {
            approved: nextApproved,
            approvedUntil: nextApprovalUntil
        })
    }

    const changeApprovalDate = (user, nextValue) => {
        sendUserUpdate(user, {
            approved: Boolean(user.approved),
            approvedUntil: nextValue ? new Date(nextValue).getTime() : null
        })
    }

    const toggleAllianceCallApproval = user => {
        sendUserUpdate(user, {
            allianceCallApproved: !Boolean(user?.allianceCallApproved)
        })
    }

    const changeAccountRole = (group, nextPrivilege) => {
        ws.send(JSON.stringify([ErrorType.Success, ActionType.SetAccountRole, {
            uuid: group.uuid,
            privilege: Number(nextPrivilege)
        }]))
    }

    const toggleCallSetupApproval = user => {
        sendUserUpdate(user, {
            callSetupApproved: !Boolean(user?.callSetupApproved)
        })
    }

    const toggleAllianceRiftPortalApproval = user => {
        if (isModeratorMode)
            return

        sendUserUpdate(user, {
            allianceRiftPortalApproved: !Boolean(user?.allianceRiftPortalApproved)
        })
    }

    const toggleGrandTournamentApproval = user => {
        if (isModeratorMode)
            return

        sendUserUpdate(user, {
            grandTournamentApproved: !Boolean(user?.grandTournamentApproved)
        })
    }

    const removeUser = user => {
        if (window.confirm(`Are you sure you want to DELETE user ${user.name}?`)) {
            const payload = { ...user, uuid: user.uuid }
            ws.send(JSON.stringify([ErrorType.Success, ActionType.RemoveUser, [payload]]))
        }
    }

    const renderUserManagementRow = (row, rowKeyPrefix = 'user-management') => {
        const approvalStatus = getApprovalStatus(row, effectiveNow)
        const approvalLabel = approvalStatus.label.startsWith('Disabled') ?
            approvalStatus.label.replace('Disabled', t('Disabled')) :
            t(approvalStatus.label)
        const approvalLocked = isModeratorMode && Boolean(row?.approved)

        return (
            <TableRow key={`${rowKeyPrefix}-${row.id}`}>
                <TableCell>
                    <Stack spacing={0.35}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {row.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {`${t('Owner username')}: ${row.ownerUsername ?? t('Unknown user')}`}
                        </Typography>
                    </Stack>
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8em' }}>{row.uuid}</TableCell>
                <TableCell>
                    <Chip
                        label={row.state ? t('Running') : t('Stopped')}
                        color={row.state ? 'success' : 'default'}
                        size="small"
                    />
                </TableCell>
                <TableCell>
                    <Stack spacing={1} sx={{ minWidth: 260 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Switch
                                checked={Boolean(row.approved)}
                                onChange={() => toggleApproval(row)}
                                color="secondary"
                                disabled={approvalLocked}
                            />
                            <Typography variant="body2">
                                {row.approved ? t('Yes') : t('No')}
                            </Typography>
                        </Stack>
                        <TextField
                            type="datetime-local"
                            size="small"
                            label={t('Approved until')}
                            value={formatDateTimeInputValue(row.approvedUntil)}
                            onChange={event => changeApprovalDate(row, event.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Stack>
                </TableCell>
                <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Switch
                            checked={Boolean(row.callSetupApproved)}
                            onChange={() => toggleCallSetupApproval(row)}
                            color="secondary"
                        />
                        <Typography variant="body2">
                            {row.callSetupApproved ? t('Yes') : t('No')}
                        </Typography>
                    </Stack>
                </TableCell>
                <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Switch
                            checked={Boolean(row.allianceCallApproved)}
                            onChange={() => toggleAllianceCallApproval(row)}
                            color="secondary"
                        />
                        <Typography variant="body2">
                            {row.allianceCallApproved ? t('Yes') : t('No')}
                        </Typography>
                    </Stack>
                </TableCell>
                <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Switch
                            checked={Boolean(row.allianceRiftPortalApproved)}
                            onChange={() => toggleAllianceRiftPortalApproval(row)}
                            color="secondary"
                            disabled={isModeratorMode}
                        />
                        <Typography variant="body2">
                            {row.allianceRiftPortalApproved ? t('Yes') : t('No')}
                        </Typography>
                    </Stack>
                </TableCell>
                <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Switch
                            checked={Boolean(row.grandTournamentApproved)}
                            onChange={() => toggleGrandTournamentApproval(row)}
                            color="secondary"
                            disabled={isModeratorMode}
                        />
                        <Typography variant="body2">
                            {row.grandTournamentApproved ? t('Yes') : t('No')}
                        </Typography>
                    </Stack>
                </TableCell>
                <TableCell>
                    <Chip
                        label={approvalLabel}
                        size="small"
                        variant="filled"
                        sx={getApprovalChipSx(approvalStatus.tone)}
                    />
                </TableCell>
                <TableCell align="right">
                    {isModeratorMode ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('Unavailable')}
                        </Typography>
                    ) : (
                        <Button color="error" size="small" onClick={() => removeUser(row)}>{t('Delete')}</Button>
                    )}
                </TableCell>
            </TableRow>
        )
    }

    return (
        <Paper
            sx={{
                p: { xs: 2, md: 4 },
                width: '100%',
                maxWidth: 'none',
                minHeight: { xs: 'calc(100vh - 112px)', md: 'calc(100vh - 126px)' },
                overflow: 'auto',
                margin: 0
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4" color="primary">{t(pageTitle)}</Typography>
                <Button variant="outlined" onClick={onClose}>{t('Close')}</Button>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
                {t(pageDescription)}
            </Alert>

            {!isModeratorMode && (
                <Paper
                    sx={{
                        p: 2.25,
                        mb: 3,
                        border: '1px solid rgba(255,255,255,0.08)',
                        backgroundColor: 'rgba(255,255,255,0.03)'
                    }}
                >
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <PersonRoundedIcon sx={{ color: 'primary.light' }} />
                            <Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {t('My Account')}
                                </Typography>
                                <Typography variant="h6">
                                    {accountInfo?.username ?? t('Unknown user')}
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ ml: { sm: 'auto' } }}>
                            <Chip
                                size="small"
                                label={`${t('Connected username')}: ${accountInfo?.username ?? t('Unknown user')}`}
                            />
                            <Chip
                                size="small"
                                color={accountInfo?.isAdmin ? 'secondary' : 'default'}
                                variant={accountInfo?.isAdmin ? 'filled' : 'outlined'}
                                label={`${t('Role')}: ${resolveRoleLabel(accountInfo?.role)}`}
                            />
                        </Stack>
                    </Stack>

                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                            {t('Linked accounts')}
                        </Typography>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                            {linkedAccounts.length > 0 ? linkedAccounts.map(name => (
                                <Chip key={name} size="small" label={name} />
                            )) : (
                                <Chip size="small" variant="outlined" label={t('No linked accounts')} />
                            )}
                        </Stack>
                    </Box>

                    {currentAccountUsers.length > 0 && (
                        <Box sx={{ mt: 2.25 }}>
                            <Typography variant="body2" sx={{ mb: 1.25, color: 'text.secondary' }}>
                                {t('Linked account details')}
                            </Typography>
                            <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('Username')}</TableCell>
                                            <TableCell>{t('UUID')}</TableCell>
                                            <TableCell>{t('Status')}</TableCell>
                                            <TableCell>{t('Approved')}</TableCell>
                                            <TableCell>{t('CALL')}</TableCell>
                                            <TableCell>{t('Alliance CALL')}</TableCell>
                                            <TableCell>{t('Alliance RIFT Portal')}</TableCell>
                                            <TableCell>{t('Grand Tournament')}</TableCell>
                                            <TableCell>{t('Time Left')}</TableCell>
                                            <TableCell align="right">{t('Actions')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {currentAccountUsers.map(row => renderUserManagementRow(row, 'current-account'))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Paper>
            )}

            <TextField
                fullWidth
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
                placeholder={t('Search approvals...')}
                sx={{ mb: 3 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchRoundedIcon sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                    )
                }}
            />

            {!isModeratorMode && accountInfo?.isAdmin && (
                <Paper
                    sx={{
                        p: 2.25,
                        mb: 3,
                        border: '1px solid rgba(255,255,255,0.08)',
                        backgroundColor: 'rgba(255,255,255,0.03)'
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 1.5 }}>
                        {t('Approval overview')}
                    </Typography>
                    <Stack spacing={1.5}>
                        {filteredApprovalGroups.length > 0 ? filteredApprovalGroups.map(group => (
                            <Box
                                key={group.key}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2.5,
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    backgroundColor: 'rgba(0,0,0,0.16)'
                                }}
                            >
                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={1}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'flex-start', md: 'center' }}
                                    sx={{ mb: 1 }}
                                >
                                    <Box>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {t('Owner username')}
                                        </Typography>
                                        <Typography variant="h6">
                                            {group.ownerUsername}
                                        </Typography>
                                    </Box>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                        <FormControl size="small" sx={{ minWidth: 188 }}>
                                            <Select
                                                value={Number(group.ownerPrivilege ?? 0)}
                                                onChange={event => changeAccountRole(group, event.target.value)}
                                                disabled={String(group.ownerUsername ?? '').trim() === 'Admin'}
                                                sx={{
                                                    borderRadius: 2,
                                                    backgroundColor: 'rgba(255,255,255,0.04)'
                                                }}
                                            >
                                                {ACCOUNT_ROLE_OPTIONS.map(option => (
                                                    <MenuItem key={`account-role-${option.value}`} value={option.value}>
                                                        {t(option.key)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Chip
                                            size="small"
                                            color={group.pendingApprovals > 0 ? 'warning' : 'success'}
                                            variant={group.pendingApprovals > 0 ? 'filled' : 'outlined'}
                                            label={`${t('Pending approvals')}: ${group.pendingApprovals}`}
                                        />
                                    </Stack>
                                </Stack>

                                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                                    {`${t('Account role')}: ${resolveRoleLabel(group.ownerRole)}`}
                                </Typography>

                                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                    {group.linkedUsers.map(linkedUser => (
                                        <Chip
                                            key={`${group.key}-${linkedUser.id}`}
                                            size="small"
                                            color={linkedUser.approved ? 'success' : 'default'}
                                            variant={linkedUser.approved ? 'outlined' : 'filled'}
                                            label={linkedUser.name}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )) : (
                            <Chip size="small" variant="outlined" label={t('No matching users')} />
                        )}
                    </Stack>
                </Paper>
            )}

            {(isModeratorMode || accountInfo?.isAdmin) && (
                <>
                    <Typography variant="h6" sx={{ mb: 2 }}>{t('User Management')}</Typography>
                    <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('Username')}</TableCell>
                                    <TableCell>{t('UUID')}</TableCell>
                                    <TableCell>{t('Status')}</TableCell>
                                    <TableCell>{t('Approved')}</TableCell>
                                    <TableCell>{t('CALL')}</TableCell>
                                    <TableCell>{t('Alliance CALL')}</TableCell>
                                    <TableCell>{t('Alliance RIFT Portal')}</TableCell>
                                    <TableCell>{t('Grand Tournament')}</TableCell>
                                    <TableCell>{t('Time Left')}</TableCell>
                                    <TableCell align="right">{t('Actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredUsers.map(row => renderUserManagementRow(row))}
                                {filteredUsers.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={10} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                            {t('No matching users')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Paper>
    )
}
